import { snapLayoutBoxToCanvasZoomGrid } from '@/lib/transforms/canvas-layout-pixel-snap'
import { withMinimumVisualShapeLayout } from '@/lib/transforms/shape-layout-visual'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

export interface LayoutBox {
  x: number
  y: number
  width: number
  height: number
}

/**
 * The shell's box in shape-local space: `pathLayoutBounds` when given, else the full placement
 * rect. Pen-drawn paths store coordinates in anchor-only space, so `pathLayoutBounds` would
 * introduce a scale/offset distortion that breaks NodeEditOverlay alignment — they always use the
 * placement rect.
 */
export function computeShellBounds(
  shape: Pick<ShapeData, 'width' | 'height' | 'path'>,
  pathLayoutBounds: ShapeBounds | null | undefined
): LayoutBox {
  const isPenPath = typeof shape.path === 'string' && shape.path.trim().length > 0
  const bounds = isPenPath ? null : pathLayoutBounds

  return {
    x: bounds?.x ?? 0,
    y: bounds?.y ?? 0,
    width: shape.width === 0 ? 0 : (bounds?.width ?? shape.width ?? 0),
    height: shape.height === 0 ? 0 : (bounds?.height ?? shape.height ?? 0),
  }
}

/**
 * The shell div's CSS box in canvas space: `shellBounds` offset by the shape position, snapped to
 * the canvas-zoom device-pixel grid (unless a resize gesture suppresses it) and grown to the
 * minimum visual size.
 */
export function computeLayoutShell(
  shapeX: number,
  shapeY: number,
  shellBounds: LayoutBox,
  canvasZoom: number | undefined,
  suppressLayoutPixelSnap: boolean
): LayoutBox {
  const raw = {
    x: shapeX + shellBounds.x,
    y: shapeY + shellBounds.y,
    width: shellBounds.width,
    height: shellBounds.height,
  }

  if (canvasZoom == null || !Number.isFinite(canvasZoom) || canvasZoom <= 0) {
    return withMinimumVisualShapeLayout(raw, 1)
  }

  if (suppressLayoutPixelSnap) {
    return withMinimumVisualShapeLayout(raw, canvasZoom)
  }

  return withMinimumVisualShapeLayout(snapLayoutBoxToCanvasZoomGrid(raw, canvasZoom), canvasZoom)
}

/**
 * The SVG viewBox, in shape-local units. The shell div's CSS box is `layoutShell` — pixel-grid
 * snapping shrinks/shifts it by up to ~1 device px from the raw `shellBounds` box (see
 * snapLayoutBoxToCanvasZoomGrid). The viewBox must track that same adjustment (relative to
 * shape.x/y), not the pre-snap `shellBounds`, or the path geometry is mapped at a slightly
 * different scale than the box it's actually rendered into — visible as a thin sliver of the
 * outline appearing to vanish right at an edge where the snap pulled the box in the most.
 */
export function computeViewBoxRect(
  layoutShell: LayoutBox,
  shapeX: number,
  shapeY: number
): LayoutBox {
  return {
    x: layoutShell.x - shapeX,
    y: layoutShell.y - shapeY,
    width: layoutShell.width,
    height: layoutShell.height,
  }
}

/**
 * CSS `transform-origin` for the shell's rotation.
 *
 * Rotation pivots on the GEOMETRIC rect's centre (NSD anchors every element at `AR="CTR"`), not on
 * the shell's own centre. The shell is sized to the measured, pixel-snapped *visual* path bounds,
 * so for any shape whose fill doesn't reach all four edges of its declared box — a triangle, a
 * star, any ellipse-inscribed polygon — those two centres are different points. Leaving CSS's
 * default `transform-origin: 50% 50%` makes such a shape orbit its visual centre: correct at 0°
 * and drifting further the more it is rotated, while the selection chrome (which already
 * compensates — see `transformOrigin` in `shape-manager/components/selection-outline-highlights.tsx`)
 * stays put. Expressed relative
 * to `layoutShell` rather than `shellBounds` so it tracks the same post-snap box the shell is
 * actually rendered at.
 */
export function computeRotationOrigin(
  shape: Pick<ShapeData, 'x' | 'y' | 'width' | 'height'>,
  layoutShell: Pick<LayoutBox, 'x' | 'y'>
): string | undefined {
  const w = shape.width ?? 0
  const h = shape.height ?? 0

  if (!(w > 0) || !(h > 0)) {
    return undefined
  }

  return `${shape.x + w / 2 - layoutShell.x}px ${shape.y + h / 2 - layoutShell.y}px`
}
